import React, { useState } from 'react'
import MaterialTable from '@material-table/core'
import { TableCell } from '@mui/material'
import * as UIcons from '@iconscout/react-unicons'
import { withTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import useSWR, { mutate } from 'swr'
import TableActionBtn from 'components/TableActionBtn'
import { apiRequest } from 'store/Misc/apiRequest'
import StatusPill from '../../shared/StatusPill'
import ConfirmDialog from '../../shared/ConfirmDialog'
import ContactForm from './ContactForm'
import ContactDetailDrawer from './ContactDetailDrawer'
import EmptyContact from './EmptyContact'

export default withTranslation()(function ContactTable({ t, q = '', type = '', status = '' }) {
  const { data } = useSWR('merchants/contacts', { revalidateOnFocus: true })
  const { data: meResponse } = useSWR('auth/me')
  const [selectedCount, setSelectedCount] = useState(0)
  const [editingContact, setEditingContact] = useState(null)
  const [viewingContact, setViewingContact] = useState(null)
  const [toggleTarget, setToggleTarget] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [busy, setBusy] = useState(false)

  const me = meResponse?.data
  const primaryContact = me && {
    id: `primary-${me.id}`,
    first_name: me.first_name,
    last_name: me.last_name,
    email: me.email,
    phone: me.phone,
    status: me.status,
    contact_type: null,
    isPrimary: true
  }

  const contacts = [...(primaryContact ? [primaryContact] : []), ...(data?.data ?? [])]

  const filtered = contacts.filter((contact) => {
    const query = q.trim().toLowerCase()
    if (query && !`${contact.first_name} ${contact.last_name}`.toLowerCase().includes(query)) return false
    if (type && contact.contact_type !== type) return false
    if (status && (contact.status ?? 'active') !== status) return false
    return true
  })

  const typeLabel = (contact) => contact.contact_type
    ? t(`settings.business_organization.contact_person.types.${contact.contact_type}`)
    : t('settings.business_organization.contact_person.primary_contact')

  const confirmToggleStatus = async () => {
    const willDisable = toggleTarget.status !== 'inactive'
    setBusy(true)
    try {
      await apiRequest({
        method: 'put',
        url: `merchants/contacts/${toggleTarget.id}`,
        payload: { status: willDisable ? 'inactive' : 'active' }
      })
      mutate('merchants/contacts')
      toast.success(t(`settings.business_organization.contact_person.success_${willDisable ? 'disable' : 'enable'}`))
      setToggleTarget(null)
      setViewingContact(null)
    } catch ({ message }) {
      toast.error(message)
    } finally {
      setBusy(false)
    }
  }

  const confirmDelete = async () => {
    setBusy(true)
    try {
      await apiRequest({ method: 'delete', url: `merchants/contacts/${deleteTarget.id}` })
      mutate('merchants/contacts')
      toast.success(t('settings.business_organization.contact_person.success_delete'))
      setDeleteTarget(null)
      setViewingContact(null)
    } catch ({ message }) {
      toast.error(message)
    } finally {
      setBusy(false)
    }
  }

  if (!contacts.length) return <EmptyContact />

  return (
    <div className="w-full bg-white rounded-xl border border-border-lines overflow-hidden">
      <MaterialTable
        columns={[
          { title: t('settings.business_organization.contact_person.columns.name'), field: 'first_name', render: (row) => `${row.first_name} ${row.last_name}` },
          { title: t('settings.business_organization.contact_person.columns.type'), field: 'contact_type', render: (row) => typeLabel(row) },
          { title: t('settings.business_organization.contact_person.columns.phone'), field: 'phone' },
          { title: t('settings.business_organization.contact_person.columns.email'), field: 'email' },
          { title: t('misc.status'), field: 'status', render: (row) => <StatusPill active={(row.status ?? 'active') === 'active'} /> }
        ]}
        data={filtered}
        actions={[{ position: 'row', tooltip: t('settings.business_organization.contact_person.view_details') }]}
        onSelectionChange={(rows) => setSelectedCount(rows.length)}
        options={{
          selection: true,
          actionsColumnIndex: -1,
          toolbar: false,
          pageSize: 10,
          pageSizeOptions: [10, 20, 50],
          headerStyle: { textTransform: 'uppercase', fontSize: '12px', color: '#748799', backgroundColor: '#F5F6FA' }
        }}
        localization={{
          header: { actions: '' },
          body: { emptyDataSourceMessage: t('misc.no_records') }
        }}
        components={{
          Pagination: (props) => {
            const totalPages = Math.max(1, Math.ceil(props.count / props.rowsPerPage))
            const currentPage = props.page + 1

            return (
              <TableCell colSpan={props.colSpan}>
                <div className="flex flex-wrap items-center justify-between gap-3 py-1">
                  <span className="text-sm text-secondary">
                    {t('settings.business_organization.contact_person.table.selected', { count: selectedCount, total: props.count })}
                  </span>

                  <div className="flex items-center gap-4">
                    <select
                      value={props.rowsPerPage}
                      onChange={(event) => props.onRowsPerPageChange(event)}
                      className="border border-border-lines rounded-lg px-2 py-1 text-sm text-dark-blue"
                    >
                      {(props.rowsPerPageOptions ?? [10, 20, 50]).map((size) => <option key={size} value={size}>{size}</option>)}
                    </select>

                    <span className="text-sm text-secondary whitespace-nowrap">
                      {t('settings.business_organization.contact_person.table.page_of', { page: currentPage, total: totalPages })}
                    </span>

                    <div className="flex items-center gap-1">
                      <button type="button" onClick={(event) => props.onPageChange(event, 0)} disabled={props.page === 0} className="p-1 rounded disabled:opacity-30">
                        <UIcons.UilAngleDoubleLeft size={16} className="text-secondary" />
                      </button>
                      <button type="button" onClick={(event) => props.onPageChange(event, props.page - 1)} disabled={props.page === 0} className="p-1 rounded disabled:opacity-30">
                        <UIcons.UilAngleLeft size={16} className="text-secondary" />
                      </button>
                      <button type="button" onClick={(event) => props.onPageChange(event, props.page + 1)} disabled={currentPage >= totalPages} className="p-1 rounded disabled:opacity-30">
                        <UIcons.UilAngleRight size={16} className="text-secondary" />
                      </button>
                      <button type="button" onClick={(event) => props.onPageChange(event, totalPages - 1)} disabled={currentPage >= totalPages} className="p-1 rounded disabled:opacity-30">
                        <UIcons.UilAngleDoubleRight size={16} className="text-secondary" />
                      </button>
                    </div>
                  </div>
                </div>
              </TableCell>
            )
          },

          Action: ({ data: row }) => (
            <TableActionBtn
              data={row.isPrimary ? [
                {
                  Content: { icon: UIcons.UilInfoCircle, text: t('settings.business_organization.contact_person.view_details') },
                  action: () => setViewingContact(row)
                }
              ] : [
                {
                  Content: { icon: UIcons.UilInfoCircle, text: t('settings.business_organization.contact_person.view_details') },
                  action: () => setViewingContact(row)
                },
                {
                  Content: { icon: UIcons.UilEditAlt, text: t('settings.business_organization.contact_person.edit') },
                  action: () => setEditingContact(row)
                },
                {
                  Content: {
                    icon: UIcons.UilBan,
                    text: t(`settings.business_organization.contact_person.${row.status !== 'inactive' ? 'disable' : 'enable'}`)
                  },
                  action: () => setToggleTarget(row)
                },
                {
                  Content: { icon: UIcons.UilTrash, text: t('settings.business_organization.contact_person.delete'), classList: 'text-failure' },
                  action: () => setDeleteTarget(row)
                }
              ]}
            />
          )
        }}
      />

      {
        editingContact && (
          <ContactForm
            contact={editingContact}
            handleClick={() => setEditingContact(null)}
            onSaved={() => mutate('merchants/contacts')}
          />
        )
      }

      {
        viewingContact && (
          <ContactDetailDrawer
            contact={viewingContact}
            onClose={() => setViewingContact(null)}
            onEdit={() => { setEditingContact(viewingContact); setViewingContact(null) }}
            onDeactivate={() => setToggleTarget(viewingContact)}
            onDelete={() => setDeleteTarget(viewingContact)}
          />
        )
      }

      {
        toggleTarget && (
          <ConfirmDialog
            title={t(`settings.business_organization.contact_person.${toggleTarget.status !== 'inactive' ? 'disable' : 'enable'}`)}
            description={t(`settings.business_organization.contact_person.${toggleTarget.status !== 'inactive' ? 'disable_confirm' : 'enable_confirm'}`)}
            confirmText={t(`settings.business_organization.contact_person.${toggleTarget.status !== 'inactive' ? 'disable' : 'enable'}`)}
            danger={toggleTarget.status !== 'inactive'}
            loading={busy}
            onConfirm={confirmToggleStatus}
            onCancel={() => setToggleTarget(null)}
          />
        )
      }

      {
        deleteTarget && (
          <ConfirmDialog
            title={t('settings.business_organization.contact_person.delete')}
            description={t('settings.business_organization.contact_person.delete_confirm')}
            confirmText={t('settings.business_organization.contact_person.delete')}
            danger
            loading={busy}
            onConfirm={confirmDelete}
            onCancel={() => setDeleteTarget(null)}
          />
        )
      }
    </div>
  )
})
